import { Dialog, DialogContent } from '@mui/material';
import { t } from 'i18next';
import { useState } from 'react';
import { MainButton } from '../../components/Buttons/MainButton';
import TextFieldFormik from '../../components/FormikForm/TextFieldFormik';
import {
  simpleModalActionsContainerStyles,
  simpleModalContentContainerStyles,
  simpleModalPaperDynamicSizeRootStyles,
} from '../../components/Modals/styles/simpleModalStyles';
import { styleWords } from '../../components/text/utils';
import { useLogout } from '../../graphql/client/useLogout';
import { useDeleteUser } from '../../graphql/apis/user/mutations/hooks/useDeleteUser';
import { colors } from '../../theme/colors';
import spacing from '../../theme/spacing';
import { fontSize } from '../../theme/typography';

interface AccountDeleteConfirmationModalProps {
  isOpen: boolean;
  handleClose: () => void;
}

const AccountDeleteConfirmationModal = ({
  isOpen,
  handleClose,
}: AccountDeleteConfirmationModalProps) => {
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const { logout } = useLogout();
  const { deleteUser, loading, error } = useDeleteUser({
    onCompleted: (data) => {
      if (data.deleteUser.success) {
        logout();
      }
    },
    onError: (error) => {
      console.error('Error deleting account:', error);
    },
  });

  const handleDeleteAccount = async () => {
    await deleteUser();
  };
  const isDeleteEnabled = deleteConfirmation === 'DELETE';

  return (
    <Dialog
      open={isOpen}
      onClose={loading ? undefined : handleClose}
      sx={{
        '& .MuiPaper-root': {
          ...simpleModalPaperDynamicSizeRootStyles,
          minWidth: 300,
          maxWidth: 400,
          width: '90%',
          margin: 0,
          alignSelf: 'center',
          padding: 0,
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent style={simpleModalContentContainerStyles}>
        <div
          style={{
            color: colors.textPrimary,
            fontSize: fontSize.lg,
            textAlign: 'center',
            fontWeight: '500',
            marginBottom: spacing.small,
          }}
        >
          {t('accountSettings.deleteAccountConfirmation')}
        </div>
        <div
          style={{
            color: colors.textPrimary,
            fontSize: fontSize.sm,
            marginBottom: spacing.medium,
            textAlign: 'center',
            marginTop: spacing.medium,
          }}
        >
          {t('accountSettings.deleteAccountWarningDetailed')}
        </div>
        <div
          style={{
            color: colors.textPrimary,
            marginBottom: spacing.small,
            marginTop: spacing.medium,
          }}
        >
          {styleWords({
            limitTimesToStyleProvidedWords: 1,
            text: t('accountSettings.deleteAccountConfirmationInstruction', {
              deleteText: 'DELETE',
            }),
            wordsToStyle: ['DELETE'],
            style: {
              fontWeight: 'bold',
            },
          })}
        </div>
        <TextFieldFormik
          id="deleteConfirmation"
          name="deleteConfirmation"
          value={deleteConfirmation}
          label={t('accountSettings.deleteConfirmation')}
          onChange={(e) => setDeleteConfirmation(e.target.value)}
          containerStyle={{ marginBottom: spacing.large }}
        />
      </DialogContent>
      <div style={simpleModalActionsContainerStyles}>
        <MainButton
          onPress={handleClose}
          typeStyle="link"
          disabled={loading}
          text={t('common.cancel')}
          textStyle={{ color: colors.textPrimary }}
        />
        <MainButton
          onPress={handleDeleteAccount}
          typeStyle="danger"
          variant="outlined"
          text={t('accountSettings.deleteThisAccount')}
          style={{ borderWidth: 0 }}
          textStyle={{ color: colors.red }}
          disabled={!isDeleteEnabled || loading}
        />
      </div>
    </Dialog>
  );
};

export default AccountDeleteConfirmationModal;
